import React from 'react';
import { motion } from 'framer-motion';
import { Check, Star, Zap, Crown, Download } from 'lucide-react';
import { useReducedMotion } from '../hooks/useReducedMotion';

const plans = [
  {
    name: 'Basic',
    price: 'Free',
    description: 'Perfect for getting started with relationship insights',
    icon: Star,
    color: 'from-gray-500 to-gray-600',
    borderColor: 'border-gray-200/30 dark:border-gray-700/30',
    features: [
      'Basic AI relationship analysis',
      'Weekly insight reports',
      'Community forum access',
      'Email support',
      'Mobile app access'
    ]
  },
  {
    name: 'Pro',
    price: '$9.99',
    period: '/month',
    description: 'Advanced features for deeper relationship understanding',
    icon: Zap,
    color: 'from-orange-500 to-pink-500',
    borderColor: 'border-orange-200/50 dark:border-orange-700/50',
    popular: true,
    features: [
      'Advanced AI insights with Gemini 2.5 Flash',
      'VR role-play scenarios',
      'Expert Q&A sessions',
      'Daily mood tracking',
      'Personalized action plans',
      'Priority support'
    ]
  },
  {
    name: 'Premium',
    price: '$19.99',
    period: '/month',
    description: 'Complete relationship transformation toolkit',
    icon: Crown,
    color: 'from-purple-500 to-indigo-500',
    borderColor: 'border-purple-200/50 dark:border-purple-700/50',
    features: [
      'Everything in Pro',
      'Apple Watch/Fitbit integration',
      'Advanced VR environments',
      'Personal relationship coach',
      'Unlimited expert consultations',
      'Premium micro-courses',
      'Family plan (up to 4 members)'
    ]
  }
];

export const Pricing: React.FC = () => {
  const prefersReducedMotion = useReducedMotion();

  const handleDownloadApp = () => {
    const downloadSection = document.getElementById('download');
    if (downloadSection) {
      downloadSection.scrollIntoView({ behavior: 'smooth' });
    } else {
      console.log('Download app clicked - would redirect to app stores');
    }
    // Trigger confetti animation
    if (!prefersReducedMotion) {
      // Confetti logic would go here
    }
  };

  return (
    <section id="pricing" className="py-24 bg-gradient-to-br from-orange-50 via-blue-50 to-purple-50 dark:from-orange-900/10 dark:via-blue-900/10 dark:to-purple-900/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          className="text-center mb-16"
          initial={prefersReducedMotion ? {} : { opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-6">
            Choose Your{' '}
            <span className="bg-gradient-to-r from-orange-500 to-pink-500 bg-clip-text text-transparent">
              Growth Path
            </span>
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
            Start your journey to better relationships with our AI-powered insights and personalized guidance
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {plans.map((plan, index) => (
            <motion.div
              key={plan.name}
              className={`relative group ${plan.popular ? 'md:-mt-4 md:mb-4' : ''}`}
              initial={prefersReducedMotion ? {} : { opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: index * 0.1 }}
              viewport={{ once: true }}
              whileHover={prefersReducedMotion ? {} : { y: -5 }}
            >
              {plan.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 z-10">
                  <span className="bg-gradient-to-r from-orange-500 to-pink-500 text-white px-4 py-1 rounded-full text-sm font-medium">
                    Most Popular
                  </span>
                </div>
              )}

              <div className={`h-full p-8 rounded-3xl bg-white/20 dark:bg-gray-800/20 backdrop-blur-xl border ${plan.borderColor} hover:border-opacity-60 transition-all duration-300 ${plan.popular ? 'ring-2 ring-orange-500/20' : ''}`}>
                {/* Icon */}
                <div className={`inline-flex p-3 rounded-2xl bg-gradient-to-r ${plan.color} mb-6`}>
                  <plan.icon className="w-6 h-6 text-white" />
                </div>

                {/* Header */}
                <div className="mb-8">
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                    {plan.name}
                  </h3>
                  <div className="flex items-baseline mb-4">
                    <span className="text-4xl font-bold text-gray-900 dark:text-white">
                      {plan.price}
                    </span>
                    {plan.period && (
                      <span className="text-gray-600 dark:text-gray-400 ml-1">
                        {plan.period}
                      </span>
                    )}
                  </div>
                  <p className="text-gray-600 dark:text-gray-300">
                    {plan.description}
                  </p>
                </div>

                {/* Features */}
                <ul className="space-y-4 mb-8">
                  {plan.features.map((feature, featureIndex) => (
                    <motion.li
                      key={feature}
                      className="flex items-start gap-3"
                      initial={prefersReducedMotion ? {} : { opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.5, delay: 0.5 + featureIndex * 0.1 }}
                      viewport={{ once: true }}
                    >
                      <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700 dark:text-gray-300">
                        {feature}
                      </span>
                    </motion.li>
                  ))}
                </ul>

                {/* CTA Button */}
                <motion.button
                  onClick={handleDownloadApp}
                  className={`w-full py-4 rounded-full font-semibold transition-all duration-300 flex items-center justify-center gap-2 ${
                    plan.popular
                      ? 'bg-gradient-to-r from-orange-500 to-pink-500 text-white hover:shadow-lg'
                      : 'bg-white/10 dark:bg-gray-800/10 text-gray-900 dark:text-white border border-gray-200/30 dark:border-gray-700/30 hover:bg-white/20 dark:hover:bg-gray-800/20'
                  }`}
                  whileHover={prefersReducedMotion ? {} : { scale: 1.02 }}
                  whileTap={prefersReducedMotion ? {} : { scale: 0.98 }}
                >
                  <Download className="w-4 h-4" />
                  Download App
                </motion.button>

                {/* Persona Overlay */}
                {plan.name === 'Pro' && (
                  <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-purple-500/5 to-pink-500/5 pointer-events-none" />
                )}
                {plan.name === 'Premium' && (
                  <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-blue-500/5 to-indigo-500/5 pointer-events-none" />
                )}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Additional Info */}
        <motion.div
          className="text-center mt-12"
          initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          viewport={{ once: true }}
        >
          <p className="text-gray-600 dark:text-gray-300 mb-4">
            All plans include a 14-day free trial. Cancel anytime.
          </p>
          <div className="flex flex-wrap justify-center gap-8 text-sm text-gray-500 dark:text-gray-400">
            <span>✓ No setup fees</span>
            <span>✓ Secure payments</span>
            <span>✓ 24/7 support</span>
            <span>✓ GDPR compliant</span>
          </div>
        </motion.div>
      </div>
    </section>
  );
};